import { useCallback, useMemo, useState } from 'react';
import type { FieldChange, SettingKey } from './settingsFormat.js';
import {
  type ConfigKey,
  type PlatformConfig,
  type SettingsValues,
  TAB_KEYS,
  type TabId,
  sameValue,
  toValues,
} from './settingsTypes.js';

/** The card each PATCH key belongs to (mirrors the backend's `SETTING_OF_KEY`). */
export const SETTING_OF_KEY: Record<ConfigKey, SettingKey> = {
  operating_hours_precedence: 'operating_hours',
  platform_fee: 'platform_fee',
  sst: 'sst',
  channel_configs: 'payment_channels',
  extension_policy: 'extension_policy',
  booking_timers: 'unpaid_bookings',
  customer_inspection_photos: 'inspection_photos',
  inspection_windows: 'inspection_photos',
  auto_workflow: 'auto_workflow',
  booking_buffer_minutes: 'booking_buffer',
  home_popup: 'home_popup',
  receipt_details: 'receipt_details',
  booking_emails: 'booking_emails',
  penalty_payment: 'penalty_payment',
  mileage_packages: 'mileage_packages',
};

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function leaves(v: unknown, prefix: string, out: Map<string, unknown>) {
  if (isObject(v)) {
    for (const [k, x] of Object.entries(v)) leaves(x, prefix ? `${prefix}.${k}` : k, out);
  } else out.set(prefix || 'value', v ?? null);
}

/** Each changed leaf, named as the history names it (`amount_minor`, `1.fee.amount_minor`, `value`). */
export function fieldChanges(before: unknown, after: unknown): FieldChange[] {
  const a = new Map<string, unknown>();
  const b = new Map<string, unknown>();
  leaves(before, '', a);
  leaves(after, '', b);
  const out: FieldChange[] = [];
  for (const field of new Set([...a.keys(), ...b.keys()])) {
    const from = a.has(field) ? a.get(field) : null;
    const to = b.has(field) ? b.get(field) : null;
    if (!sameValue(from, to)) out.push({ field, from, to });
  }
  return out;
}

function setLeaf(target: Record<string, unknown>, path: string[], v: unknown) {
  let node = target;
  for (const p of path.slice(0, -1)) {
    if (!isObject(node[p])) node[p] = {};
    node = node[p] as Record<string, unknown>;
  }
  node[path[path.length - 1] as string] = v;
}

/**
 * The page's edit applied to a newer saved value: every leaf the page changed (draft differs from the
 * base it was made against) wins; every other leaf takes the newer saved value.
 */
export function rebase(base: unknown, draft: unknown, saved: unknown): unknown {
  if (sameValue(base, saved)) return draft;
  if (!isObject(draft) || !isObject(saved)) return draft;
  const out = structuredClone(saved) as Record<string, unknown>;
  for (const c of fieldChanges(base, draft)) {
    if (c.field === 'value') return draft;
    setLeaf(out, c.field.split('.'), c.to);
  }
  return out;
}

export interface SaveRequest {
  keys: ConfigKey[];
  body: Record<string, unknown>;
}

/**
 * 356 (research R11): the page's edits, held per PATCH key against what the platform served.
 *
 * - A key is dirty while its draft differs from the saved value; editing back clears it (FR-007).
 * - `saveRequest(tab)` builds ONE PATCH for the tab's dirty keys, with `if_unchanged` holding the values
 *   the page loaded, so all of a tab's changes save together or not at all (FR-009, FR-010).
 * - For payment channels only the changed channels are sent, as the backend expects.
 */
export function useSettingsDraft(config: PlatformConfig | undefined) {
  const saved = useMemo(() => (config ? toValues(config) : null), [config]);
  // Each edit remembers the saved value it was made against, so that when the saved values change
  // underneath it (a reload after someone else's save) only the fields THIS page edited win.
  const [drafts, setDrafts] = useState<
    Partial<Record<ConfigKey, { base: unknown; value: unknown }>>
  >({});

  /** The edited values, each rebased onto the current saved value. */
  const effective = useMemo(() => {
    const out: Partial<SettingsValues> = {};
    if (!saved) return out;
    for (const [k, d] of Object.entries(drafts) as Array<
      [ConfigKey, { base: unknown; value: unknown }]
    >) {
      (out as Record<string, unknown>)[k] = rebase(d.base, d.value, saved[k]);
    }
    return out;
  }, [drafts, saved]);

  const value = useCallback(
    <K extends ConfigKey>(key: K): SettingsValues[K] | null => {
      if (key in effective) return effective[key] as SettingsValues[K];
      return saved ? saved[key] : null;
    },
    [effective, saved],
  );

  const set = useCallback(
    <K extends ConfigKey>(key: K, next: SettingsValues[K]) => {
      setDrafts((d) => ({ ...d, [key]: { base: saved ? saved[key] : null, value: next } }));
    },
    [saved],
  );

  const dirtyKeys = useMemo(
    () =>
      saved
        ? (Object.keys(effective) as ConfigKey[]).filter((k) => !sameValue(effective[k], saved[k]))
        : [],
    [effective, saved],
  );

  const isDirty = useCallback((key: ConfigKey) => dirtyKeys.includes(key), [dirtyKeys]);

  const dirtyIn = useCallback(
    (tab: TabId) => dirtyKeys.filter((k) => TAB_KEYS[tab].includes(k)),
    [dirtyKeys],
  );

  const changesOf = useCallback(
    (key: ConfigKey): FieldChange[] =>
      saved && isDirty(key) ? fieldChanges(saved[key], effective[key]) : [],
    [saved, effective, isDirty],
  );

  /** Forget the edits to these keys (Discard, or after a save). */
  const reset = useCallback((keys: ConfigKey[]) => {
    setDrafts((d) => {
      const next = { ...d };
      for (const k of keys) delete next[k];
      return next;
    });
  }, []);

  const saveRequest = useCallback(
    (tab: TabId): SaveRequest | null => {
      if (!saved) return null;
      const keys = dirtyIn(tab);
      if (keys.length === 0) return null;
      const body: Record<string, unknown> = {};
      const ifUnchanged: Record<string, unknown> = {};
      for (const k of keys) {
        if (k === 'channel_configs') {
          const now = (effective.channel_configs ?? {}) as Record<string, unknown>;
          const was = (saved.channel_configs ?? {}) as Record<string, unknown>;
          const changed = Object.keys(now).filter((id) => !sameValue(now[id], was[id] ?? null));
          body[k] = Object.fromEntries(changed.map((id) => [id, now[id]]));
          ifUnchanged[k] = Object.fromEntries(changed.map((id) => [id, was[id] ?? null]));
        } else {
          body[k] = effective[k];
          ifUnchanged[k] = saved[k];
        }
      }
      return { keys, body: { ...body, if_unchanged: ifUnchanged } };
    },
    [saved, effective, dirtyIn],
  );

  return { saved, value, set, dirtyKeys, isDirty, dirtyIn, changesOf, reset, saveRequest };
}

export type SettingsDraft = ReturnType<typeof useSettingsDraft>;
